import { Component, type ErrorInfo, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { colorTokens } from "../theme/tokens";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Where the secondary button navigates (a full navigation, not a router
   * link: the boundary already caught a render crash, so it does not lean
   * on React Router's own navigation working correctly either). Defaults
   * to "/", the signed-in/signed-out router's own entry point.
   */
  homeTo?: string;
  /** Defaults to "Back to your files" (manager decision: role-specific
   * callers, staff's AppShell boundary in particular, override this to
   * "Back to sharings"). */
  homeLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * A blank page is worse than an error message (manager report:
 * StaffSharingSettingsTab's detail.members.map(...) crashing on a null
 * array took the whole React tree down with it, with nothing on screen
 * and no way out but a manual reload). This is the last line of defence
 * for any render crash a defensive fix elsewhere missed: a friendly,
 * Leeft-styled message with a way back, never a blank page.
 *
 * Only a class component can implement getDerivedStateFromError /
 * componentDidCatch (no hook equivalent exists yet). The caught error is
 * logged to the console only, for local troubleshooting: nothing is sent
 * to any external reporting service.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Unhandled render error", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign(this.props.homeTo ?? "/");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const homeLabel = this.props.homeLabel ?? "Back to your files";

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 420, textAlign: "center", alignItems: "center" }}>
          <Typography variant="h6" component="h1" sx={{ color: colorTokens.brand, fontWeight: 700 }}>
            Something went wrong on this page.
          </Typography>
          <Typography color="text.secondary">
            Nothing was lost. Reloading the page, or going back, usually fixes it.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            <Button variant="contained" onClick={this.handleReload}>
              Reload
            </Button>
            <Button variant="outlined" onClick={this.handleGoHome}>
              {homeLabel}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
